import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, MailCheck, RefreshCw, LogOut } from 'lucide-react';
import { useAuth, needsEmailConfirm } from '../context/AuthContext';
import PasswordInput from './PasswordInput';
import '../pages/AuthPages.css';

// Pages an unconfirmed person can still open: the confirm link itself (and
// "This wasn't me"), the
// legal pages, deleting their account, password reset, and the staff portal.
const OPEN_PATHS = /^\/(confirm-email|not-me|privacy|terms|delete-account|forgot-password|reset-password|marakin-admin)(\/|$)/;
const RESEND_COOLDOWN_S = 60;

/**
 * B-65 (Stuart, 15 Oct 2026): strict email confirmation. A signed-in person
 * whose email isn't confirmed sees this screen instead of the app until they
 * use the link we emailed — new password sign-ups straight away ("Check your
 * inbox"), existing accounts at their next sign-in or app load.
 *
 * Frontend only, on purpose: the server still treats unconfirmed accounts as
 * normal (installed 1.2 store builds can't show this screen, so a server
 * block would strand them). Phones get it with the next store build.
 */
const EmailConfirmGate = ({ children }) => {
  const { user, impersonating } = useAuth();
  const location = useLocation();
  if (!needsEmailConfirm(user, impersonating) || OPEN_PATHS.test(location.pathname)) return children;
  return <CheckInboxScreen />;
};

const CheckInboxScreen = () => {
  const { user, logout, refreshUser, justRegistered, changeEmail: changeEmailCall } = useAuth();
  const navigate = useNavigate();
  // A sign-up in this tab was emailed a moment ago: start the Resend wait.
  const [cooldown, setCooldown] = useState(justRegistered ? RESEND_COOLDOWN_S : 0);
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [code, setCode] = useState('');
  const asked = useRef(false);

  const startCooldown = (s = RESEND_COOLDOWN_S) => setCooldown(Math.max(1, Math.ceil(s)));
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendFailed = (err, fallback) => {
    const d = err?.response?.data || {};
    if (d.retryAfter) startCooldown(d.retryAfter);
    setError(d.error || fallback);
  };

  // An existing account (or a new one whose sign-up email failed) may have no
  // live link: ask once on show. The server keeps a link that still works.
  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    axios.post('/auth/confirm-email/send', { onlyIfNoneLive: true })
      .then(res => {
        if (res.data?.verified) refreshUser();
        else if (res.data?.sent) startCooldown();
      })
      .catch(err => { if (err?.response?.status === 429) sendFailed(err, ''); });
  // eslint-disable-next-line
  }, []);

  // They'll usually confirm in another tab or the mail app: re-check when the
  // app comes back into view.
  const recheck = useCallback(async () => {
    const fresh = await refreshUser();
    return fresh;
  }, [refreshUser]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') recheck(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [recheck]);

  const resend = async () => {
    setError(''); setInfo(''); setBusy(true);
    try {
      const res = await axios.post('/auth/confirm-email/send');
      if (res.data?.verified) { await refreshUser(); return; }
      setInfo(`We've sent a new link to ${user.email}.`);
      startCooldown();
    } catch (err) {
      sendFailed(err, 'We couldn\'t send the email. Please try again.');
    } finally { setBusy(false); }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setBusy(true);
    try {
      // Needs the current password (security review); other devices are signed out.
      const res = await changeEmailCall({ email: newEmail.trim(), currentPassword });
      setChanging(false); setNewEmail(''); setCurrentPassword('');
      setInfo(`Email changed. We've sent a link to ${res?.email || newEmail.trim()}.`);
      startCooldown();
    } catch (err) {
      sendFailed(err, 'We couldn\'t change your email. Please try again.');
    } finally { setBusy(false); }
  };

  const continueIfConfirmed = async () => {
    setError(''); setInfo(''); setBusy(true);
    try {
      const fresh = await recheck();
      if (fresh && fresh.emailVerified === false) {
        setError('We haven\'t seen your confirmation yet. Tap the link in the email, then come back here.');
      }
    } finally { setBusy(false); }
  };

  // B-59: a club invite is waiting, so a 6-digit code was emailed too. It
  // confirms the email just as the link does.
  const verifyCode = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setBusy(true);
    try {
      await axios.post('/auth/email-code/verify', { code });
      await refreshUser();
    } catch (err) {
      setError(err?.response?.data?.error || 'That code didn\'t work. Please try again.');
    } finally { setBusy(false); }
  };

  const signOut = () => { logout(); navigate('/login', { replace: true }); };

  return (
    <div className="auth-page">
      <div className="auth-card confirm-gate" data-testid="email-confirm-gate">
        <div className="auth-logo-wrap">
          <img src="/marakin-logo-charcoal.png" alt="Marakin" className="auth-logo-img" />
        </div>
        <div className="confirm-gate-icon"><MailCheck size={26} /></div>
        {justRegistered ? (
          <>
            <h1>Check your inbox</h1>
            <p className="auth-subtitle">
              We've sent a link to <strong className="confirm-gate-email">{user.email}</strong>. Tap it to confirm your email and get started.
            </p>
          </>
        ) : (
          <>
            <h1>One quick thing: confirm your email</h1>
            <p className="auth-subtitle">
              Nothing's wrong with your account. We now ask everyone to confirm their email once, to keep accounts safe.
              It's one click: we've sent a link to <strong className="confirm-gate-email">{user.email}</strong>.
            </p>
          </>
        )}

        {info && <p className="social-note" role="status">{info}</p>}
        {error && <div className="auth-error" role="alert"><AlertCircle size={16} /> {error}</div>}

        <button type="button" className="btn btn-primary auth-submit" onClick={continueIfConfirmed} disabled={busy}>
          I've confirmed, continue
        </button>
        <button type="button" className="btn btn-secondary auth-submit confirm-gate-resend" onClick={resend} disabled={busy || cooldown > 0}>
          <RefreshCw size={15} /> {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend email'}
        </button>
        <p className="confirm-gate-hint">Can't see it? Check your spam folder.</p>

        {user.invitesWaiting > 0 && (
          <form onSubmit={verifyCode} className="auth-form confirm-gate-section">
            <div className="form-group">
              <label className="label" htmlFor="confirm-gate-code">Got a 6-digit code from us instead?</label>
              <input id="confirm-gate-code" className="input" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                placeholder="123456" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
            </div>
            <button type="submit" className="btn btn-secondary auth-submit" disabled={busy || code.length !== 6}>Confirm with code</button>
          </form>
        )}

        {changing ? (
          <form onSubmit={changeEmail} className="auth-form confirm-gate-section">
            <div className="form-group">
              <label className="label" htmlFor="confirm-gate-new-email">New email address</label>
              <input id="confirm-gate-new-email" className="input" type="email" autoComplete="email" required
                placeholder="you@example.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="label" htmlFor="confirm-gate-password">Your password</label>
              <PasswordInput id="confirm-gate-password" autoComplete="current-password" required
                value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
            </div>
            <button type="submit" className="btn btn-primary auth-submit" disabled={busy || cooldown > 0 || !newEmail.trim() || !currentPassword}>
              {cooldown > 0 ? `Send to this address in ${cooldown}s` : 'Send to this address'}
            </button>
            <button type="button" className="social-back" onClick={() => { setChanging(false); setError(''); }}>Cancel</button>
          </form>
        ) : null}

        {/* WT-10: two separate links that wrap cleanly on a phone (no "·"
            left dangling at the end of a line), each a full-height tap target. */}
        <div className="auth-switch confirm-gate-links">
          {!changing && <button type="button" className="social-back" onClick={() => { setChanging(true); setError(''); setInfo(''); }}>Wrong address? Change email</button>}
          <button type="button" className="social-back" onClick={signOut}><LogOut size={13} /> Sign out</button>
        </div>
      </div>
    </div>
  );
};

export default EmailConfirmGate;
